/**
 * Clean facade for defects operations
 * Single responsibility: read/write defects via useStorageState
 * Ensures canonical field names and proper event emission
 */

import { devLog } from '../../../utils/devLog';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { filterTombstoned } from '../../../sync/tombstone';  // FT-458: hide soft-deleted defects from read getters

const makeId = () => (crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2)}`);
const normStr = (v, d = '') => (v ?? d).toString().trim();

// Map severity to risk level that DefectsContent expects
const mapSeverityToRiskLevel = (severity) => {
  const map = {
    'critical': 'Critical',
    'high': 'High',
    'medium': 'Medium',
    'low': 'Low'
  };
  return map[severity?.toLowerCase()] || 'Medium';
};

export function useDefects() {
  const { getCurrentVessel } = useVessel();
  const vessel = getCurrentVessel();
  const { value: raw = [], setValue } = useStorageState('defects', []); // ✅ base key only
  
  // Always work with arrays
  const defects = Array.isArray(raw) ? raw : [];

  // FT-458: display-facing view with soft-deleted (tombstoned) defects removed.
  // The read GETTERS (list/getOpen/getByStatus/getByEquipment) return this so a
  // deleted defect never shows or counts. Mutations + findById + the FT-353f
  // corrective-job cascade keep using raw `defects` so the tombstone is
  // preserved on write and back-references can still be resolved.
  const visibleDefects = filterTombstoned(defects);

  /**
   * Add a defect with canonical fields
   * Ensures the defect will be properly displayed in DefectsModule
   */
  const addDefect = (input = {}) => {
    const now = Date.now();
    const id = makeId();

    // Build defect object with EXACT field names DefectsContent expects
    const defect = {
      // Required fields with canonical formats per Anchor Mode
      id,
      status: 'open',  // LOWERCASE - canonical format
      createdAt: now,  // Epoch ms - canonical timestamp
      date_reported: new Date(now).toISOString(), // DefectsContent expects this field
      date_created: new Date(now).toISOString(), // Keep for compatibility
      
      // Source and linkage
      source: input.source || 'maintenance',
      record_type: 'defect', // NOT 'repair' - this is a defect
      
      // Main content - description is what's displayed
      description: normStr(input.title || input.description || input.defectDetails, 'Maintenance-related defect'),
      title: normStr(input.title || input.description || input.defectDetails, 'Maintenance-related defect'),
      
      // Risk/Priority with field names DefectsContent expects
      risk_level: mapSeverityToRiskLevel(input.severity || 'medium'),
      severity: normStr(input.severity, 'medium'),
      priority: normStr(input.priority, 'normal'),
      category: normStr(input.category, 'equipment'),
      
      // Equipment/Location
      equipment_linked: input.equipment_affected || input.equipmentType || '',
      equipment_affected: input.equipment_affected || input.equipmentType || '',
      location: input.location || '',
      
      // Who reported
      reported_by: input.reported_by || input.reportedBy || input.inspector || 'Unknown',
      
      // Maintenance linkage if from maintenance
      ...(input.source === 'maintenance' && {
        linkedMaintenanceRef: {
          jobTitle: normStr(input.jobTitle),
          jobId: input.jobId || null,
          equipmentType: normStr(input.equipmentType, 'unknown'),
        }
      })
      
      // NO vesselId - storage layer handles scoping per Anchor Mode
    };

    devLog('[useDefects] =====================================');
    devLog('[useDefects] ADDING DEFECT TO STORAGE');
    devLog('[useDefects] Defect summary:', {
      id: defect.id,
      description: defect.description,
      status: defect.status,
      risk_level: defect.risk_level,
      equipment_linked: defect.equipment_linked,
      source: defect.source
    });
    devLog('[useDefects] Full defect object:', defect);
    devLog('[useDefects] =====================================')

    // Single write through storage hook
    setValue(prev => {
      const list = Array.isArray(prev) ? prev : [];
      devLog('[useDefects] Current defects before add:', list);
      devLog('[useDefects] Current defects count:', list.length);
      const updated = [...list, defect]; // append = always a real change
      devLog('[useDefects] New defects after add:', updated);
      devLog('[useDefects] New defects count:', updated.length);
      
      // Extra verification
      const justAdded = updated.find(d => d.id === id);
      if (justAdded) {
        devLog('[useDefects] ✅ Defect successfully added to array:', justAdded.id);
      } else {

      }
      
      return updated;
    });

    return id;
  };

  /**
   * Get all defects (FT-458: tombstoned excluded)
   */
  const list = () => visibleDefects;

  /**
   * Get open defects only - handle both canonical and legacy formats
   * (FT-458: tombstoned excluded)
   */
  const getOpen = () => visibleDefects.filter(d => {
    const normalized = (d.status || '').toLowerCase();
    return normalized === 'open';
  });

  /**
   * Find defect by ID (raw — must resolve a tombstoned record for cascades)
   */
  const findById = (did) => defects.find(d => d.id === did);

  /**
   * Get defects by status (FT-458: tombstoned excluded)
   */
  const getByStatus = (status) => visibleDefects.filter(d => d.status === status);

  /**
   * Get defects for specific equipment (FT-458: tombstoned excluded)
   */
  const getByEquipment = (equipmentType) => {
    const normalized = normStr(equipmentType).toLowerCase();
    return visibleDefects.filter(d => {
      const defectEquipment = normStr(d.equipment_affected).toLowerCase();
      return defectEquipment === normalized;
    });
  };

  /**
   * FT-209: Update a defect with new fields
   * Used to link correctiveJobId when generating corrective job
   */
  const updateDefect = (defectId, updates) => {
    if (!defectId) {
      devLog('[useDefects] updateDefect: No defectId provided');
      return false;
    }

    setValue(prev => {
      const list = Array.isArray(prev) ? prev : [];
      const index = list.findIndex(d => d.id === defectId);

      if (index === -1) {
        devLog('[useDefects] updateDefect: Defect not found:', defectId);
        return list;
      }

      const updated = [...list];
      updated[index] = {
        ...updated[index],
        ...updates,
        updatedAt: Date.now()
      };

      devLog('[useDefects] updateDefect: Updated defect:', defectId, updates);
      return updated;
    });

    return true;
  };

  /**
   * FT-209: Link a corrective job to a defect
   * Sets correctiveJobId and updates status to 'in progress'
   */
  const linkCorrectiveJob = (defectId, jobId, userName = 'Unknown') => {
    return updateDefect(defectId, {
      correctiveJobId: jobId,
      status: 'in progress',
      history: [
        ...(findById(defectId)?.history || []),
        {
          date: new Date().toISOString(),
          action: 'Corrective job generated',
          by: userName,
          notes: `Created maintenance job ${jobId}`
        }
      ]
    });
  };

  /**
   * FT-353f: Cascade handler called when a corrective maintenance job is
   * deleted / archived. Clears the defect's back-reference and reverts
   * status ONLY if the defect is `in progress` AND no other progression
   * happened since the corrective job was generated.
   *
   * Safeguard rationale: if the crew has appended a note, photo, or
   * status change since the "Corrective job generated" audit entry, that
   * work should not be lost — leave status alone but still drop the link.
   *
   * @param {string} jobId — the id of the deleted maintenance job
   * @param {string} userName — actor for the audit history entry
   * @returns {boolean} true if a defect was found + updated, false otherwise
   */
  const handleCorrectiveJobDeleted = (jobId, userName = 'Unknown') => {
    if (!jobId) return false;

    // Find the defect whose correctiveJobId points at this job.
    const defect = defects.find(d => {
      const back = typeof d.correctiveJobId === 'string'
        ? d.correctiveJobId
        : d.correctiveJobId?.id || null;
      return back === jobId;
    });

    if (!defect) {
      devLog('[useDefects] handleCorrectiveJobDeleted: no defect back-references job', jobId);
      return false;
    }

    // Determine whether the status revert safeguard fires. We look at
    // the LAST history entry: if it is the "Corrective job generated"
    // record from linkCorrectiveJob and status is currently 'in progress',
    // no other progression has been layered on top so it's safe to
    // revert to 'open'. Any subsequent note/photo/status entry aborts
    // the revert.
    const history = Array.isArray(defect.history) ? defect.history : [];
    const lastEntry = history[history.length - 1];
    const statusLower = (defect.status || '').toLowerCase();
    const isInProgress = statusLower === 'in progress' || statusLower === 'in_progress';
    const onlyGenerationEntry =
      lastEntry &&
      lastEntry.action === 'Corrective job generated' &&
      (lastEntry.notes || '').includes(jobId);
    const shouldRevertStatus = isInProgress && !!onlyGenerationEntry;

    const updates = {
      correctiveJobId: null,
      history: [
        ...history,
        {
          date: new Date().toISOString(),
          action: 'Corrective job removed',
          by: userName,
          notes: `Job ${jobId} deleted${shouldRevertStatus ? ' — status reverted to Open' : ''}`
        }
      ]
    };
    if (shouldRevertStatus) {
      // Preserve casing convention of the existing status field. Legacy
      // records store 'Open' (title case) while newer ones use lowercase.
      updates.status = /[A-Z]/.test(defect.status || '') ? 'Open' : 'open';
    }

    updateDefect(defect.id, updates);
    return true;
  };

  return {
    list,
    getOpen,
    findById,
    getByStatus,
    getByEquipment,
    addDefect,
    updateDefect,
    linkCorrectiveJob,
    handleCorrectiveJobDeleted,
    // FT-458: the exposed `defects` is now the DISPLAY view (tombstoned removed)
    // — dashboard/compliance/upcoming/categories consumers read `.defects` for
    // counts and must not see soft-deleted records. Internal writes use
    // setValue(prev=>...) on the raw storage array, so tombstones are preserved.
    defects: visibleDefects,
    defectsWithDeleted: defects // FT-458: raw array incl. tombstones (admin/restore/cascade)
  };
}